import { useState, useRef, useEffect } from 'react';
import {
  Play,
  RotateCcw,
  Settings,
  Sparkles,
  Clock,
  Database,
  FileText,
  Zap,
  TrendingUp,
  Copy,
  Check,
} from 'lucide-react';

export function QueryPlayground() {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [queryHistory, setQueryHistory] = useState<Array<{ query: string; timestamp: string }>>([]);
  const [showHistory, setShowHistory] = useState(false);
  const queryTimerRef = useRef<number | null>(null);
  const copyTimerRef = useRef<number | null>(null);

  const [settings, setSettings] = useState({
    temperature: 0.7,
    maxTokens: 2048,
    topK: 5,
    similarityThreshold: 0.75,
    includeMetadata: true,
  });

  const exampleQueries = [
    'What are the key differences between RAG and Agentic RAG?',
    'Explain the Multi-Agent RAG architecture',
    'How do MCP servers work with agents?',
    'What planning strategies can agents use?',
  ];

  const handleRunQuery = async () => {
    if (!query.trim()) return;

    setIsLoading(true);

    // Add to query history
    const newHistory = [
      { query: query, timestamp: new Date().toLocaleTimeString() },
      ...queryHistory.slice(0, 9)
    ];
    setQueryHistory(newHistory);

    // Simulate API call with settings applied
    if (queryTimerRef.current) {
      clearTimeout(queryTimerRef.current);
    }
    queryTimerRef.current = window.setTimeout(() => {
      const baseLatency = 1000;
      const tempAdjustment = settings.temperature * 500;
      const tokenAdjustment = (settings.maxTokens / 2048) * 500;
      const simLatency = baseLatency + tempAdjustment + tokenAdjustment;

      setResponse({
        answer: `Based on the retrieved context, here's a comprehensive explanation:

Multi-Agent RAG is an advanced architecture that uses multiple specialized AI agents working together. Unlike Basic RAG which uses simple retrieval, or Agentic RAG with a single agent, Multi-Agent RAG employs:

1. **Aggregator Agent**: Coordinates other specialist agents
2. **Specialist Agents**: Each focused on specific tasks (search, analysis, synthesis)
3. **Memory Systems**: Both short-term (conversation context) and long-term (persistent knowledge)
4. **Planning Strategies**: Including ReACT and Chain of Thought
5. **MCP Servers**: Access to local data, search engines, and cloud services

This architecture enables more sophisticated reasoning and better handling of complex queries.`,
        responseTime: `${(simLatency / 1000).toFixed(2)}s`,
        tokensUsed: Math.min(settings.maxTokens, 487 + Math.floor(Math.random() * 200)),
        relevanceScore: Math.max(0.7, Math.min(0.99, settings.similarityThreshold + Math.random() * 0.2)),
        settingsUsed: {
          temperature: settings.temperature,
          maxTokens: settings.maxTokens,
          topK: settings.topK,
          similarityThreshold: settings.similarityThreshold,
        },
        sources: [
          {
            id: 1,
            title: 'Introduction to RAG Architectures',
            snippet: 'RAG systems can be classified into three main types: Basic RAG, Agentic RAG, and Multi-Agent RAG...',
            relevance: 0.94,
            source: 'course_materials/rag_overview.pdf',
          },
          {
            id: 2,
            title: 'Multi-Agent Systems in AI',
            snippet: 'Multi-agent architectures leverage multiple specialized agents that communicate and coordinate...',
            relevance: 0.91,
            source: 'course_materials/multi_agent.pdf',
          },
          {
            id: 3,
            title: 'MCP Server Integration',
            snippet: 'Model Context Protocol servers enable agents to access external data sources and tools...',
            relevance: 0.89,
            source: 'course_materials/mcp_integration.pdf',
          },
        ],
        metadata: {
          model: 'gpt-4-turbo-preview',
          vectorDb: 'Pinecone',
          chunksRetrieved: 5,
          agentsUsed: ['Aggregator', 'Search Agent', 'Analysis Agent'],
        },
      });
      setIsLoading(false);
    }, 2000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current);
    }
    copyTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (queryTimerRef.current) {
        clearTimeout(queryTimerRef.current);
      }
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">RAG Query Playground</h1>
        <p className="text-sm text-gray-600 mt-1">
          Test your RAG system with real queries and see detailed results
        </p>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Query Area */}
        <div className="col-span-2 space-y-6">
          {/* Query Input */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-blue-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Sparkles className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Enter Your Query</h2>
              </div>
            </div>
            <div className="p-6">
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask anything about your indexed materials..."
                rows={4}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all resize-none"
              />
              <div className="flex items-center justify-between mt-4">
                <div className="text-xs text-gray-500">
                  {query.length} characters
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setQuery('')}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Clear
                  </button>
                  <button
                    onClick={handleRunQuery}
                    disabled={!query.trim() || isLoading}
                    className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Run Query
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Example Queries & History */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">
                {showHistory ? 'Query History' : 'Try These Examples'}
              </h3>
              {queryHistory.length > 0 && (
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  {showHistory ? 'Show Examples' : `View History (${queryHistory.length})`}
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {showHistory ? (
                queryHistory.length > 0 ? (
                  queryHistory.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => setQuery(item.query)}
                      className="text-left text-sm p-3 bg-white rounded-lg border border-orange-100 hover:border-fk-orange-vibrant300 hover:shadow-sm transition-all"
                    >
                      <p className="line-clamp-2 mb-1">{item.query}</p>
                      <p className="text-xs text-gray-500">{item.timestamp}</p>
                    </button>
                  ))
                ) : (
                  <p className="col-span-2 text-sm text-gray-500 text-center py-4">No query history yet</p>
                )
              ) : (
                exampleQueries.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setQuery(example)}
                    className="text-left text-sm p-3 bg-white rounded-lg border border-orange-100 hover:border-fk-orange-vibrant300 hover:shadow-sm transition-all"
                  >
                    {example}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Response */}
          {response && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Sparkles className="w-5 h-5 text-green-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">Response</h2>
                  </div>
                  <button
                    onClick={() => copyToClipboard(response.answer)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3 h-3 mr-1 text-green-600" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{response.answer}</p>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
                  <div className="text-center">
                    <Clock className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Response Time</p>
                    <p className="text-sm font-semibold text-gray-900">{response.responseTime}</p>
                  </div>
                  <div className="text-center">
                    <Zap className="w-5 h-5 text-#ea7500 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Tokens Used</p>
                    <p className="text-sm font-semibold text-gray-900">{response.tokensUsed}</p>
                  </div>
                  <div className="text-center">
                    <TrendingUp className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Relevance Score</p>
                    <p className="text-sm font-semibold text-gray-900">{(response.relevanceScore * 100).toFixed(0)}%</p>
                  </div>
                  <div className="text-center">
                    <Database className="w-5 h-5 text-orange-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Sources Found</p>
                    <p className="text-sm font-semibold text-gray-900">{response.sources.length}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sources */}
          {response && response.sources && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Retrieved Sources</h3>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {response.sources.map((source: any) => (
                  <div key={source.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 flex-1">{source.title}</h4>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full ml-3">
                        {(source.relevance * 100).toFixed(0)}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{source.snippet}</p>
                    <p className="text-xs text-gray-500">📄 {source.source}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Settings Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-3">
                <Settings className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Query Settings</h3>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Temperature
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={settings.temperature}
                    onChange={(e) => setSettings({ ...settings, temperature: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Precise (0)</span>
                    <span className="font-semibold text-gray-900">{settings.temperature}</span>
                    <span>Creative (1)</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Max Tokens
                </label>
                <input
                  type="number"
                  value={settings.maxTokens}
                  onChange={(e) => setSettings({ ...settings, maxTokens: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Top K Chunks
                </label>
                <input
                  type="number"
                  value={settings.topK}
                  onChange={(e) => setSettings({ ...settings, topK: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Number of chunks to retrieve</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Similarity Threshold
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.similarityThreshold}
                    onChange={(e) => setSettings({ ...settings, similarityThreshold: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Less strict</span>
                    <span className="font-semibold text-gray-900">{settings.similarityThreshold}</span>
                    <span>More strict</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.includeMetadata}
                    onChange={(e) => setSettings({ ...settings, includeMetadata: e.target.checked })}
                    className="rounded text-primary-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Include Metadata</span>
                </label>
              </div>
            </div>
          </div>

          {/* Metadata */}
          {response && settings.includeMetadata && (
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900">Query Metadata</h3>
              </div>
              <div className="p-6 space-y-3 text-sm">
                <div className="bg-blue-50 rounded-lg p-3 mb-3">
                  <p className="text-xs font-medium text-blue-900 mb-2">Settings Applied</p>
                  <div className="space-y-1 text-xs text-blue-700">
                    <p>Temperature: {response.settingsUsed.temperature}</p>
                    <p>Max Tokens: {response.settingsUsed.maxTokens}</p>
                    <p>Top K: {response.settingsUsed.topK}</p>
                    <p>Similarity: {response.settingsUsed.similarityThreshold}</p>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Model:</span>
                  <span className="font-medium text-gray-900">{response.metadata.model}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Vector DB:</span>
                  <span className="font-medium text-gray-900">{response.metadata.vectorDb}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Chunks Retrieved:</span>
                  <span className="font-medium text-gray-900">{response.metadata.chunksRetrieved}</span>
                </div>
                <div className="pt-3 border-t border-gray-100">
                  <span className="text-gray-600 block mb-2">Agents Used:</span>
                  <div className="space-y-1">
                    {response.metadata.agentsUsed.map((agent: string, idx: number) => (
                      <div key={idx} className="text-xs px-2 py-1 bg-fk-orange-vibrant50 text-#d66a00 rounded inline-block mr-2">
                        {agent}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
